/**
 * Version Checker Service
 *
 * Polls the version.json file to detect when a new version has been deployed.
 * Shows a notification to the user prompting them to refresh the page.
 */

let currentVersion = null;
let intervalId = null;
let onNewVersionCallback = null;

// Default polling interval: 60 seconds
const DEFAULT_INTERVAL_MS = 60000;

/**
 * Fetch the current version from the server
 */
async function fetchVersion() {
  try {
    // Add timestamp to bust cache
    const response = await fetch(`/admin/version.json?t=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Version check failed:', error);
    return null;
  }
}

/**
 * Check if a new version is available
 */
async function checkVersion() {
  const serverVersion = await fetchVersion();

  if (!serverVersion) {
    return;
  }

  if (currentVersion === null) {
    // First load, just store the version
    currentVersion = serverVersion.git.commit;
    console.debug('Version checker initialized:', currentVersion);
  } else if (currentVersion !== serverVersion.git.commit) {
    // Version changed! Trigger callback
    console.debug('New version detected:', serverVersion.git.commit, 'Current:', currentVersion);
    if (onNewVersionCallback) {
      onNewVersionCallback(serverVersion);
    }
    // Stop polling after detecting new version (user needs to refresh)
    stopVersionChecker();
  }
}

/**
 * Start the version checker
 * @param {Function} callback - Function to call when new version is detected
 * @param {number} intervalMs - Polling interval in milliseconds (default: 60000)
 */
export function startVersionChecker(callback, intervalMs = DEFAULT_INTERVAL_MS) {
  onNewVersionCallback = callback;

  // Check immediately on init
  checkVersion();

  // Handle visibility change - pause polling when tab is hidden
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Start polling if tab is visible
  if (document.visibilityState === 'visible') {
    startPolling(intervalMs);
  }
}

/**
 * Handle tab visibility changes
 */
function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    // Tab became visible - check immediately and resume polling
    checkVersion();
    startPolling(DEFAULT_INTERVAL_MS);
  } else {
    // Tab hidden - pause polling to save resources
    stopPolling();
  }
}

/**
 * Start the polling interval
 */
function startPolling(intervalMs) {
  if (!intervalId) {
    intervalId = setInterval(checkVersion, intervalMs);
  }
}

/**
 * Stop the polling interval
 */
function stopPolling() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

/**
 * Stop the version checker completely
 */
export function stopVersionChecker() {
  stopPolling();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  onNewVersionCallback = null;
}

/**
 * Get the current stored version
 */
export function getCurrentVersion() {
  return currentVersion;
}
